import { useCallback, useRef, useState } from 'react';
import { Logger } from '@saviynt/common';

import { type ViolationMitigatingControls } from '../data-layer/detailsTypes';
import { detailsActions } from '../data-store/detailsSlice';

// Cache to store mitigating control data by violationId
const mitigatingControlCache: Record<string, ViolationMitigatingControls[]> =
  {};

/**
 * Custom hook for handling mitigating control row selection
 * @returns {Object} An object containing mitigating control handling methods
 * @returns {Function} returns.loadMitigateRow - Function to load mitigating control data
 * @returns {any} returns.selectedMitigateRow - Currently selected mitigating control row
 * @returns {any[]} returns.mitigatingControlRows - List of mitigating control rows from API
 * @returns {boolean} returns.isLoading - Loading state for API calls
 * @returns {boolean} returns.isError - Error state for API calls
 *
 * @example
 * const { loadMitigateRow, selectedMitigateRow, mitigatingControlRows, isLoading, isError } = useHandleMitigatingControl();
 * loadMitigateRow(rowData);
 */
const useHandleMitigatingControl = () => {
  const [selectedMitigateRow, setSelectedMitigateRow] =
    useState<ViolationMitigatingControls | null>(null);
  const [mitigatingControlRows, setMitigatingControlRows] = useState<
    ViolationMitigatingControls[]
  >([]);
  const [getViolationMitigatingControl, { isError, isLoading }] =
    detailsActions.useGetViolationMitigatingControlMutation();

  const functionAbortRef = useRef<((reason: string) => void) | null>(null);

  /**
   * Loads mitigating control data for the given row
   * @param {object} rowData - The row data of the mitigating control
   * @returns {Promise<void>} Promise that resolves when loading is complete
   */
  const loadMitigateRow = useCallback(
    async (rowData: { id?: number | string; violationId?: string }) => {
      if (typeof rowData.id === 'undefined') {
        setSelectedMitigateRow(null);

        return;
      }

      const violationId = rowData.violationId ?? String(rowData.id);

      // Check if we already have data for this violation ID in the cache
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (mitigatingControlCache[violationId]) {
        setMitigatingControlRows(mitigatingControlCache[violationId]);

        const cachedData = mitigatingControlCache[violationId];

        if (cachedData.length > 0) {
          setSelectedMitigateRow(cachedData[0]);
        }

        return;
      }

      // Don't set selectedMitigateRow here, wait for API response
      functionAbortRef.current?.('New Selection');

      try {
        const controller = getViolationMitigatingControl({ violationId });

        functionAbortRef.current = () => controller.abort();

        const response = await controller.unwrap();
        const typedResponse = Array.isArray(response)
          ? (response as ViolationMitigatingControls[])
          : [];

        // Store in cache
        mitigatingControlCache[violationId] = typedResponse;

        setMitigatingControlRows(typedResponse);

        // Set the selected row from the API response if available
        if (typedResponse.length > 0) {
          setSelectedMitigateRow(typedResponse[0]);
        }
      } catch (error) {
        Logger.error('Failed to fetch mitigating control details', error);
        setMitigatingControlRows([]);
      }
    },
    [getViolationMitigatingControl]
  );

  return {
    loadMitigateRow,
    selectedMitigateRow,
    mitigatingControlRows,
    isLoading,
    isError,
  } as const;
};

// eslint-disable-next-line import/prefer-default-export
export { useHandleMitigatingControl };
